/**
 * Guest join — auto-connects with invite ticket; hang up leaves the room.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { validateInviteTicket } from "../api/signalingApi";
import useRoomSession from "../webrtc/useRoomSession";
import { MediaToggles } from "../ui/MediaToggles";

function randomPeerId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `guest-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function statusBannerStyle(status) {
  const base = {
    marginBottom: 14,
    padding: "12px 14px",
    borderRadius: 10,
    fontSize: 14,
    lineHeight: 1.45,
  };
  switch (status) {
    case "connecting":
      return { ...base, background: "#fffbeb", border: "1px solid #fcd34d", color: "#92400e" };
    case "waiting":
      return { ...base, background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" };
    case "in_call":
      return { ...base, background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46" };
    case "left":
      return { ...base, background: "#f3f4f6", border: "1px solid #d1d5db", color: "#374151" };
    case "error":
      return { ...base, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" };
    default:
      return { ...base, background: "#f9fafb", border: "1px solid #e5e7eb", color: "#4b5563" };
  }
}

function statusLabel(status) {
  switch (status) {
    case "connecting":
      return "Connecting";
    case "waiting":
      return "In room — waiting";
    case "in_call":
      return "In call";
    case "left":
      return "Left room";
    case "error":
      return "Error";
    default:
      return "Idle";
  }
}

export default function GuestJoin() {
  const [params] = useSearchParams();
  const ticket = (params.get("ticket") || "").trim();
  const [roomId, setRoomId] = useState("");
  const [peerId] = useState(() => randomPeerId());
  const [displayName, setDisplayName] = useState("Guest");
  const [role] = useState("client");
  const [validateError, setValidateError] = useState("");
  const [ready, setReady] = useState(false);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const logBoxRef = useRef(null);

  useEffect(() => {
    document.title = "Join room";
  }, []);

  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!ticket) {
        setValidateError("Missing invite ticket in URL.");
        return;
      }
      try {
        const data = await validateInviteTicket({ ticket });
        if (!mounted) return;
        setRoomId(data.roomId || "");
        setReady(true);
      } catch (e) {
        if (mounted) setValidateError(e?.message || String(e));
      }
    }
    run();
    return () => {
      mounted = false;
    };
  }, [ticket]);

  const getIdToken = useMemo(() => async () => "", []);

  const session = useRoomSession({
    roomId,
    peerId,
    displayName,
    role,
    getIdToken,
    localVideoRef,
    remoteVideoRef,
    inviteTicket: ticket,
    autoConnect: Boolean(ready && roomId && ticket),
  });

  useEffect(() => {
    if (logBoxRef.current) {
      logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
    }
  }, [session.logLines]);

  if (validateError) {
    return (
      <div style={{ padding: 24, maxWidth: 520, margin: "40px auto" }}>
        <h1>Cannot join</h1>
        <p style={{ color: "#991b1b" }}>{validateError}</p>
      </div>
    );
  }

  if (!ready || !roomId) {
    return <div style={{ padding: 24 }}>Validating invite…</div>;
  }

  return (
    <div style={{ maxWidth: 960, margin: "0 auto", padding: 16 }}>
      <h1 style={{ marginTop: 0 }}>Join room</h1>
      <p style={{ color: "#6b7280" }}>
        Guest invite — joining and starting the call automatically. Leave
        exits the room.
      </p>

      <div style={statusBannerStyle(session.sessionStatus)} role="status">
        <strong>{statusLabel(session.sessionStatus)}</strong>
        {session.statusDetail ? (
          <div style={{ marginTop: 4 }}>{session.statusDetail}</div>
        ) : null}
        {session.sessionStatus === "left" ? (
          <div style={{ marginTop: 10 }}>
            <button type="button" onClick={session.rejoin} style={styles.btn}>
              Rejoin
            </button>
            <p style={{ marginTop: 8, color: "#6b7280", fontSize: 13 }}>
              Rejoin uses the same invite ticket if it has not expired.
            </p>
          </div>
        ) : null}
      </div>

      <div style={styles.row}>
        <div>
          <strong>Room:</strong> {roomId}
        </div>
        <div style={{ fontSize: 13, color: "#4b5563" }}>
          <strong>Media caps:</strong> A {session.audioKbps} / V {session.videoKbps} kbps
          {session.mediaPolicySource ? ` (${session.mediaPolicySource})` : ""}
        </div>
        <label>
          Display name{" "}
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={session.inRoom || session.connecting}
            style={styles.input}
          />
        </label>
      </div>

      <div style={{ ...styles.row, marginTop: 12 }}>
        {session.sessionStatus === "left" || session.sessionStatus === "error" ? (
          <button type="button" onClick={session.rejoin} style={styles.btn}>
            Rejoin
          </button>
        ) : null}
        <button
          type="button"
          onClick={session.hangup}
          disabled={!session.canHangup}
          aria-disabled={!session.canHangup}
          style={{
            ...styles.btn,
            ...(session.canHangup ? styles.danger : styles.left),
          }}
        >
          {session.sessionStatus === "left" ? "Left" : "Leave"}
        </button>
        <MediaToggles
          cameraOn={session.cameraOn}
          micOn={session.micOn}
          canMedia={session.canMedia}
          onToggleCamera={session.toggleCamera}
          onToggleMic={session.toggleMic}
        />
      </div>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 8 }}>
        Camera and Mic are toggles — green means on; muted mic shows red Off.
      </p>

      <div style={styles.videoGrid}>
        <div>
          <strong>Local</strong>
          <video ref={localVideoRef} playsInline autoPlay muted style={styles.video} />
        </div>
        <div>
          <strong>Remote</strong>
          <video ref={remoteVideoRef} playsInline autoPlay style={styles.video} />
        </div>
      </div>
      <div ref={logBoxRef} style={styles.log}>
        {session.logLines.join("\n")}
      </div>
    </div>
  );
}

const styles = {
  row: { display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" },
  input: { marginLeft: 6, padding: "6px 8px" },
  btn: {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #d1d5db",
    background: "#e5e7eb",
    cursor: "pointer",
  },
  danger: { background: "#ef4444", color: "#fff", borderColor: "#dc2626" },
  left: {
    background: "#e5e7eb",
    color: "#9ca3af",
    borderColor: "#d1d5db",
    cursor: "not-allowed",
    opacity: 0.85,
  },
  videoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginTop: 16,
  },
  video: {
    width: "100%",
    maxWidth: 420,
    background: "#000",
    borderRadius: 8,
    display: "block",
  },
  log: {
    marginTop: 16,
    background: "#111",
    color: "#ddd",
    padding: 10,
    borderRadius: 8,
    height: 180,
    overflow: "auto",
    whiteSpace: "pre-wrap",
    fontFamily: "ui-monospace, monospace",
    fontSize: 12,
  },
};
